import { useCallback } from 'react';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useBacklogDispatch, useBacklogSelector } from '../../../../state/index.tsx';
import {
	hasDismissedBanner as hasDismissedBannerSelector,
	hasDismissedWhiteboardsEntrypoint as hasDismissedWhiteboardsEntrypointSelector,
} from '../../../../state/selectors/whiteboards-entrypoint-experiment/index.tsx';
import type { UserPropertyKeyNames } from '../../constants.tsx';
import { setUserPropertyKey } from '../../../../state/actions/whiteboards-entrypoint-experiment/index.tsx';

type AdControlsFunctionality = {
	hasDismissed: boolean;
	hasDismissedBanner: boolean;
	dismiss: (userPropertyKey: UserPropertyKeyNames) => Promise<void>;
};

export const useAdControls = (): AdControlsFunctionality => {
	const hasDismissed = useBacklogSelector(hasDismissedWhiteboardsEntrypointSelector);
	const hasDismissedBanner = useBacklogSelector(hasDismissedBannerSelector);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const dispatch = useBacklogDispatch();
	const accountId = useAccountId();

	const dismiss = useCallback(
		async (userPropertyKey: UserPropertyKeyNames) => {
			try {
				dispatch(setUserPropertyKey(userPropertyKey));
				if (accountId) {
					await setUserProperties(accountId, userPropertyKey, JSON.stringify(true));
				}
			} catch (error: unknown) {
				fireErrorAnalytics({
					meta: {
						id: 'whiteboards-set-user-property',
						packageName: 'src/packages/software/backlog',
						teamName: 'starburst',
					},
					attributes: {
						key: userPropertyKey,
						message: 'Unable to update user property for whtieboards backlog touchpoint',
						statusCode: error instanceof FetchError ? error.statusCode : null,
						traceId: error instanceof FetchError ? error.traceId : null,
					},
				});
			}

			const clickedButtonBaseEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});
			fireUIAnalytics(clickedButtonBaseEvent, 'notInterested');
		},
		[createAnalyticsEvent, accountId, dispatch],
	);

	return { hasDismissed, hasDismissedBanner, dismiss };
};
